/* ==========================================================================
   Clapstore — homepage hero.

   This file does as little as possible on purpose. The hero holds the LCP,
   so anything it does before first paint is a cost paid by every visitor.

   What it does NOT do:
     * It does not animate the first slide in. The crossfade transition is
       only enabled once `is-live` is added, which happens after the first
       frame — so the LCP image paints immediately and the fade only ever
       applies to slides 2+.
     * It does not run at all when there is one slide, which is the current
       state. A single-slide hero costs zero JS work.

   Rotation is paused on hover, on keyboard focus, when the tab is hidden, and
   is disabled outright for anyone who has asked for reduced motion or is on
   Save-Data / a 2g connection — the same consent gate used by cs-reels.js.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = '[data-cs-hero]';

  /* Framed as consent, not capability: each of these is the visitor telling us
     they do not want motion or do not want the data. */
  function motionAllowed() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c) {
      if (c.saveData) return false;
      if (/^(slow-)?2g$/.test(c.effectiveType || '')) return false;
    }
    return true;
  }

  function Hero(root) {
    this.root = root;
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-cs-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-cs-hero-dot]'));
    this.nav = root.querySelector('[data-cs-hero-nav]');
    this.pause = root.querySelector('[data-cs-hero-pause]');
    this.pauseLabel = root.querySelector('[data-cs-hero-pause-label]');

    this.index = 0;
    this.timer = 0;
    this.paused = false;

    if (this.slides.length) this.paint();

    // One slide: nothing to drive. Leave the nav hidden and do no work at all.
    if (this.slides.length < 2) return;

    this.interval = parseInt(root.dataset.csInterval, 10) || 7000;
    this.wants = root.dataset.csAutoplay === 'true' && motionAllowed();

    if (this.nav) this.nav.hidden = false;

    /* Enable the crossfade only AFTER the first paint. Doing it on the next
       frame is what keeps the LCP image from fading in. */
    var self = this;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { root.classList.add('is-live'); });
    });

    this.dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        self.go(i);
        self.stop();          // a deliberate choice beats the timer
        self.setPaused(true);
      });
    });

    if (this.pause) {
      this.pause.addEventListener('click', function () {
        self.setPaused(!self.paused);
      });
    }

    // Hovering or tabbing into the hero means the visitor is reading it.
    ['mouseenter', 'focusin'].forEach(function (evt) {
      root.addEventListener(evt, function () { self.stop(); });
    });
    ['mouseleave', 'focusout'].forEach(function (evt) {
      root.addEventListener(evt, function () { if (!self.paused) self.start(); });
    });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.stop();
      else if (!self.paused) self.start();
    });

    if (this.wants) this.start();
    else this.setPaused(true, true);
  }

  /* The area either side of a capped slide belongs to .cs-hero, not to the
     slide, so it cannot inherit --slide-bg. Lift the active slide's ground onto
     the root instead. One property write per rotation. */
  Hero.prototype.paint = function () {
    var el = this.slides[this.index];
    var bg = el.style.getPropertyValue('--slide-bg');
    var ink = el.style.getPropertyValue('--slide-ink');
    if (bg) this.root.style.setProperty('--hero-bg', bg.trim());
    /* The nav sits outside the slides, so it cannot inherit --slide-ink. Lift
       it too and the dots simply take whatever colour that slide's type uses —
       cream on the blue slide, Klein Blue on the pale ones. That replaces the
       translucent capsule the dots used to need, which turned muddy the moment
       a slide had a light ground. */
    if (ink) this.root.style.setProperty('--hero-ink', ink.trim());
  };

  Hero.prototype.go = function (next) {
    if (next === this.index) return;
    var slides = this.slides;

    slides[this.index].classList.remove('is-active');
    slides[this.index].setAttribute('aria-hidden', 'true');

    this.index = (next + slides.length) % slides.length;

    slides[this.index].classList.add('is-active');
    slides[this.index].removeAttribute('aria-hidden');

    this.dots.forEach(function (dot, i) {
      dot.setAttribute('aria-selected', i === this.index ? 'true' : 'false');
    }, this);

    this.paint();
  };

  Hero.prototype.start = function () {
    if (!this.wants || this.timer) return;
    var self = this;
    this.timer = window.setInterval(function () {
      self.go(self.index + 1);
    }, this.interval);
  };

  Hero.prototype.stop = function () {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = 0;
  };

  Hero.prototype.setPaused = function (paused, quiet) {
    this.paused = paused;
    if (paused) this.stop();
    else this.start();

    if (!this.pause) return;
    this.pause.setAttribute('aria-pressed', paused ? 'true' : 'false');
    if (this.pauseLabel) {
      this.pauseLabel.textContent = paused ? 'Play the slideshow' : 'Pause the slideshow';
    }
    /* When motion is refused rather than chosen, the control still reads
       correctly but we never announce a state change on load. */
    if (quiet) this.pause.setAttribute('aria-pressed', 'true');
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT);
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.csHeroReady === '1') return;
      root.dataset.csHeroReady = '1';
      new Hero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); }, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
